import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import type { EnhancedToolResult, ImageContent, TextContent } from '../types';

import { Tool } from './Tool';

import { ApiService, IncludeEnum, TaskResultMessageTypeEnum } from 'js/src/client';
import WebSocketClient from 'js/src/libs/websocket-client';
import { isErrorObject } from 'js/src/types';

/** Ceiling mirroring the backend's, so an over-large ask is refused before the round-trip. */
const MAX_PAGES_PER_REQUEST = 20;

interface ExtractedPageText {
    page: number;
    text: string;
}

interface ExtractedImage {
    page?: number | null;
    uuid: string;
    url?: string | null;
}

interface DocumentExtractResponse {
    metadata: {
        document_name?: string;
        has_page_structure?: boolean;
        page_count: number | null;
        pages: {
            page: number;
            title: string;
            printed_label?: string | null;
            has_text: boolean;
            embedded_image_count?: number;
        }[];
        page_labels_differ_from_ordering: boolean;
    };
    text?: ExtractedPageText[];
    screenshots?: ExtractedImage[];
    embedded_images?: ExtractedImage[];
    ignored_pages?: number[];
    truncated_to?: number;
    text_unavailable_reason?: string;
    screenshots_unavailable_reason?: string;
}

export const documentExtractSchema = z.object({
    document_id: z
        .number()
        .int()
        .positive()
        .describe('ID of the attached document, as given in its ### DOCUMENT: ... ### message.'),
    pages: z
        .array(z.number().int().positive())
        .optional()
        .describe(
            'Page numbers to read, 1-based. Omit to get a cheap listing of every page, ' +
                'which is how you choose what to request.',
        ),
    include: z
        .array(z.nativeEnum(IncludeEnum))
        .optional()
        .describe(
            "What to get for those pages: 'text' (the words), 'screenshot' (a picture of " +
                "the page as it looks) or 'embedded_images' (the images placed on it, on " +
                "their own). Defaults to 'text'.",
        ),
});
export type DocumentExtractArgs = z.infer<typeof documentExtractSchema>;

/**
 * Reads an attached document a few pages at a time.
 *
 * Nothing but a document's text is pulled out when it is attached, so a deck's pages
 * are not sitting in context waiting to be used — this is how the agent goes and gets
 * the ones it needs. Requested screenshots come back as images the agent can see, and
 * are registered as media so the same page can then be placed in the video.
 */
export class DocumentExtractTool extends Tool<typeof documentExtractSchema> {
    protected override readonly schema = documentExtractSchema;
    protected override readonly toolName = TOOL_NAMES.documentExtract;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription =
        'Read an attached document (PDF, PowerPoint, Word) page by page.';

    public override canRunAsync = true;

    public override getToolDisplayName(): string {
        return 'Reading document';
    }

    public get statusIcon(): string {
        return 'description';
    }

    public async execute(args: DocumentExtractArgs): Promise<EnhancedToolResult> {
        const { document_id: documentId, pages, include } = args;

        if (pages && pages.length > MAX_PAGES_PER_REQUEST) {
            return {
                result: `Error: ${pages.length} pages requested, but at most ${MAX_PAGES_PER_REQUEST} can be read at once. Request them in smaller batches.`,
                isSuccess: false,
            };
        }

        try {
            const response = await ApiService.documentExtractCreate({
                body: {
                    document_id: documentId,
                    ...(pages ? { pages } : {}),
                    ...(include ? { include } : {}),
                },
            });

            const taskId = response.data?.task_id;
            if (!taskId) {
                return { result: 'Error: failed to start document extraction.', isSuccess: false };
            }

            const ws = WebSocketClient.getInstance();
            const result = await ws.waitForMessage<DocumentExtractResponse>(
                taskId,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'extract_document_pages',
            );

            return await this.buildResult(documentId, result);
        } catch (error) {
            const message = isErrorObject(error)
                ? error.error.message
                : error instanceof Error
                  ? error.message
                  : 'Unknown error occurred';
            return { result: `Error reading document: ${message}`, isSuccess: false };
        }
    }

    private async buildResult(
        documentId: number,
        result: DocumentExtractResponse,
    ): Promise<EnhancedToolResult> {
        const summary: string[] = [];
        const content: (TextContent | ImageContent)[] = [];

        // Named by the server, which has the document row -- rather than by the agent
        // repeating a filename back to us, which it could get wrong or omit.
        const documentName = result.metadata.document_name || `document ${documentId}`;

        const {
            page_count: pageCount,
            pages,
            page_labels_differ_from_ordering: labelsDiffer,
        } = result.metadata;
        if (pageCount !== null && pageCount !== undefined) {
            summary.push(`${documentName}: ${pageCount} pages.`);
        } else if (result.metadata.has_page_structure) {
            // The server reads this type by page and still produced no count, so opening
            // the file is what failed. Guessing that from the filename made a legacy .ppt
            // and an ordinary .docx both read as corrupt; the server knows which is which.
            summary.push(
                `${documentName}: could not be read. The file has pages, but they could not be ` +
                    `opened — it may be corrupt, password-protected, or still uploading. Tell the ` +
                    `user this rather than building from the extracted text alone.`,
            );
        } else {
            summary.push(
                `${documentName}: this file type has no page structure, so its text can only be ` +
                    `read as the whole text that came with the attachment. A screenshot of a ` +
                    `given page can still be requested.`,
            );
        }

        if (labelsDiffer) {
            // Surfaced rather than resolved: only the user knows which numbering they
            // meant, and quietly picking one gets the wrong pages.
            const duplicated = duplicatedPrintedLabels(pages);
            summary.push(
                'CAREFUL: the numbers printed on these pages do NOT match their position in ' +
                    'the file. "Page N" from the user probably means the page PRINTED with N, ' +
                    'which is listed below — not the Nth page. Match on the printed number, and ' +
                    'confirm the page you get is the one they described before using it.',
            );
            if (duplicated.length > 0) {
                // Numbering restarts partway through (a section or appendix begins again at
                // 1), so a printed number names more than one page and cannot be resolved
                // by reading alone.
                summary.push(
                    `The numbering restarts, so ${duplicated.length === 1 ? 'this printed number appears' : 'these printed numbers appear'} ` +
                        `on more than one page: ${duplicated.join(', ')}. If the user names one of those, ` +
                        `ask which they mean (${TOOL_NAMES.askUserQuestion}), offering the candidate pages you found.`,
                );
            }
        }

        // Only when nothing specific was asked for: this is the "what is in this
        // document" answer, and printing it alongside requested pages buries them.
        // Skipped entirely when there are no pages to list, rather than leaving a
        // heading with nothing under it.
        if (!result.text && !result.screenshots && !result.embedded_images && pages.length > 0) {
            const anyPagePrintsANumber = pages.some((page) => page.printed_label);
            summary.push('What each page contains:');
            for (const page of pages) {
                // An unnumbered page is called out only when its neighbours are numbered:
                // it is usually a cover, and it is why the numbering runs behind.
                const label = page.printed_label
                    ? ` [printed as "${page.printed_label}"]`
                    : anyPagePrintsANumber
                      ? ' [prints no number]'
                      : '';
                const imageCount = page.embedded_image_count ?? 0;
                const images =
                    imageCount === 0
                        ? ''
                        : ` — ${imageCount} embedded image${imageCount === 1 ? '' : 's'}`;
                summary.push(
                    `  Page ${page.page}${label}: ${page.title || '(no text on this page)'}${images}`,
                );
            }
        }

        if (result.ignored_pages?.length) {
            summary.push(
                `Pages outside the document were ignored: ${result.ignored_pages.join(', ')}.`,
            );
        }
        if (result.truncated_to) {
            summary.push(`Only the first ${result.truncated_to} requested pages were read.`);
        }

        if (result.text_unavailable_reason) {
            summary.push(result.text_unavailable_reason);
        }

        for (const page of result.text ?? []) {
            summary.push(`\n--- Page ${page.page} ---\n${page.text || '(no text on this page)'}`);
        }

        const mediaIdsByPage = await this.registerImages([
            ...(result.screenshots ?? []),
            ...(result.embedded_images ?? []),
        ]);

        // An image with no URL cannot be shown. Saying "screenshot of page 8" anyway
        // claims something the reader can plainly see is absent, so report the failure.
        for (const shot of result.screenshots ?? []) {
            if (!shot.url) {
                summary.push(`Page ${shot.page}: the screenshot could not be produced.`);
                continue;
            }
            const mediaId = mediaIdsByPage.get(shot.uuid);
            content.push({
                type: 'text',
                text: `Page ${shot.page} of ${documentName}${mediaId ? ` (media id ${mediaId})` : ''}:`,
            });
            content.push({ type: 'image_url', image_url: { url: shot.url } });
            summary.push(
                `Screenshot of page ${shot.page} is attached above${mediaId ? ` — media id ${mediaId}` : ''}.`,
            );
        }

        for (const image of result.embedded_images ?? []) {
            if (!image.url) {
                continue;
            }
            const mediaId = mediaIdsByPage.get(image.uuid);
            content.push({
                type: 'text',
                text: `Image from page ${image.page} of ${documentName}${mediaId ? ` (media id ${mediaId})` : ''}:`,
            });
            content.push({ type: 'image_url', image_url: { url: image.url } });
            summary.push(
                `Image from page ${image.page} is attached above${mediaId ? ` — media id ${mediaId}` : ''}.`,
            );
        }

        // Asked for images but none could be produced: say so rather than returning a
        // page listing that looks like a normal, successful answer.
        // A page can carry images that cannot be used -- a JPEG 2000, say, which the
        // upload path skips -- so an empty result here does not mean the page is bare.
        if (result.embedded_images?.length === 0 && (result.screenshots?.length ?? 0) > 0) {
            summary.push(
                'None of the images embedded in these pages could be extracted (some image ' +
                    'formats are not supported). The page screenshot still shows them in place.',
            );
        }

        const askedForImages =
            result.screenshots !== undefined || result.embedded_images !== undefined;
        if (askedForImages && content.length === 0) {
            // The specific reason when there is one -- a server that cannot convert Office
            // files says nothing about this document, and retrying or re-uploading will
            // not help, so it should not read as though the file were at fault.
            summary.push(
                result.screenshots_unavailable_reason ??
                    'No images could be produced for the requested pages.',
            );
            summary.push('Do not describe these pages as though you have seen them.');
        }

        const summaryText = summary.join('\n');
        if (content.length === 0) {
            return { result: summaryText, isSuccess: true };
        }

        return {
            result: summaryText,
            resultContent: [{ type: 'text', text: summaryText }, ...content],
            isSuccess: true,
        };
    }

    /**
     * Registers every returned image as media in one pass, so a page the agent has
     * looked at can be placed in the video by id without searching for it again.
     */
    private async registerImages(images: ExtractedImage[]): Promise<Map<string, string>> {
        const uuids = images.map((image) => image.uuid).filter(Boolean);
        if (uuids.length === 0) {
            return new Map();
        }

        const ids = await MediaRegistry.getInstance().addItemsByUuids(uuids);
        const byUuid = new Map<string, string>();
        uuids.forEach((uuid, index) => {
            const id = ids[index];
            if (id) {
                byUuid.set(uuid, id);
            }
        });
        return byUuid;
    }
}

/**
 * Printed page numbers that name more than one page, which happens when a document's
 * numbering restarts partway through. Such a number cannot be resolved by reading the
 * pages -- both candidates legitimately carry it -- so the user has to say which.
 */
function duplicatedPrintedLabels(pages: { printed_label?: string | null }[]): string[] {
    const counts = new Map<string, number>();
    for (const page of pages) {
        const label = page.printed_label;
        if (label) {
            counts.set(label, (counts.get(label) ?? 0) + 1);
        }
    }
    return [...counts.entries()].filter(([, count]) => count > 1).map(([label]) => label);
}
